import React, { useState } from 'react';
import { Building2, ChevronRight, Download, ChevronDown, FileText, Sheet, Printer, Share2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { api } from '../../../utils/api';
import { projectStatus } from '../../../lib/projectVisuals';
import { materialsToCSV, downloadBlob } from '../material/materialUtils';

const latestPlan = async (projectId) => {
  const r = await api.getMaterialPlans(projectId);
  const plan = (r.data?.material_plans || []).find((p) => p.status === 'completed');
  if (!plan) throw new Error('none');
  return plan;
};

const HeaderExport = ({ project }) => {
  const slug = project.name.replace(/\s+/g, '_');
  const run = async (fn) => {
    try { await fn(); toast.success('Export klar'); } catch (e) { toast.error(e.message === 'none' ? 'Gör materialberäkningen först' : 'Exporten misslyckades'); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="op-btn-ghost" data-testid="project-export-btn"><Download size={16} />Exportera<ChevronDown size={14} className="text-stone-400" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl">
        <DropdownMenuItem data-testid="project-export-pdf" onClick={() => run(async () => { const p = await latestPlan(project.id); const r = await api.downloadMaterialPlanPdf(p.id); downloadBlob(r.data, `Materialplan_${slug}.pdf`); })}>
          <FileText size={15} className="mr-2" />Materialplan (PDF)</DropdownMenuItem>
        <DropdownMenuItem data-testid="project-export-excel" onClick={() => run(async () => { const p = await latestPlan(project.id); downloadBlob(new Blob([materialsToCSV(p)], { type: 'text/csv;charset=utf-8' }), `Materiallista_${slug}.csv`); })}>
          <Sheet size={15} className="mr-2" />Materiallista (Excel)</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="project-export-print" onClick={() => window.print()}><Printer size={15} className="mr-2" />Skriv ut sidan</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ProjectHeader = ({ project, steps, onGoTab, onQuoteDone, onShare }) => {
  const [busy, setBusy] = useState(false);
  const st = projectStatus(project);
  const updated = project.updated_at || project.created_at;

  const generateQuote = async () => {
    const missing = steps.find((s) => !s.done);
    if (missing) { toast.info(`Klara ${missing.label} först — offerten byggs från Material → Arbete → Tid.`); onGoTab(missing.tab); return; }
    setBusy(true);
    try {
      await api.generateProjectQuote(project.id);
      toast.success('Offerten är genererad — granska och skicka den i Översikt.');
      onQuoteDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte generera offert'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-4" data-testid="project-header">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <span className="w-12 h-12 rounded-2xl bg-[#F26B1F] text-white flex items-center justify-center flex-shrink-0 shadow-[0_12px_26px_-14px_rgba(242,107,31,1)]"><Building2 size={22} /></span>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-[28px] font-bold leading-tight truncate" data-testid="project-name">{project.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12.5px] text-stone-500">
            {project.customer && <><span>{project.customer}</span><ChevronRight size={12} /></>}
            {project.location && <><span>{project.location}</span><ChevronRight size={12} /></>}
            <span className="rounded-md bg-stone-200/70 px-1.5 py-0.5 text-stone-600">Projekt-ID: {project.id.slice(0, 8).toUpperCase()}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <span className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold ${st.cls}`} data-testid="project-status-pill"><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}</span>
        {updated && <div className="hidden md:block text-[11.5px] leading-tight text-stone-500 mr-2"><p>Senast uppdaterad</p><p className="font-semibold text-stone-800">{new Date(updated).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}</p></div>}
        {project.setup_status === 'started' && <button onClick={onShare} className="op-btn-ghost" data-testid="share-project-btn"><Share2 size={15} />Dela</button>}
        <HeaderExport project={project} />
        <button onClick={generateQuote} disabled={busy} className="op-btn-primary" data-testid="generate-quote-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}Generera offert
        </button>
      </div>
    </div>
  );
};
